import { useState } from 'react';
import invariant from 'tiny-invariant';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { useBoardContext } from 'business/quality-control/pages/record-segment-defect/context/board-context';
import {
  fetchDefectReportPictureV1ControllerDeleteBatch,
  fetchDefectReportPictureV1ControllerUpload,
  fetchDefectReportV1ControllerUpdateDefectReports,
  fetchRepairReportPictureV1ControllerDeleteBatch,
  fetchRepairReportPictureV1ControllerUpload,
  fetchRepairReportV1ControllerUploadRepairs,
  fetchRingValidationV1ControllerUploadRingCompliance,
} from 'generated/apiComponents';
import { Module_Enum } from 'generated/graphql';
import { utilsDbService } from 'technical/db/db.service';
import { defectReportPictureSyncService } from 'technical/db/sync/defect-report-picture-sync.service';
import { defectReportSyncService } from 'technical/db/sync/defect-report-sync.service';
import { repairReportPictureSyncService } from 'technical/db/sync/repair-report-picture-sync.service';
import { repairReportSyncService } from 'technical/db/sync/repair-report-sync.service';
import { ringSyncService } from 'technical/db/sync/ring-sync.service';
import { base64DataToBlob } from 'technical/picture-upload-input';

const BATCH_SIZE = 500;
// value should be equal or lower than the Back constraint
// see back/custom-server/src/business/quality/defect-report-picture/v1/defect-report-picture.controller.ts:upload
// see back/custom-server/src/business/quality/repair-report-picture/v1/repair-report-picture.controller.ts:upload
// to compress image, see front/src/technical/picture-upload-input/index.tsx:compressImage
const PICTURE_BATCH_SIZE = 5;
const MAX_RETRY = 25; // to update if BATCH_SIZE is changed
// this value is found with max number of segments for 1000 rings, devided by BATCH_SIZE (9000 / 500) + margin

export const useUploadQualityData = () => {
  const [isUploading, setIsUploading] = useState(false);

  const { currentConstructionSiteId, user } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site');
  const restHeaders = useRestApiHeaders(Module_Enum.QualityControl);
  const { setBoardConstructionSiteId } = useBoardContext();

  const upload = async () => {
    setIsUploading(true);
    try {
      let cpt = 0; // prevent infinite loop
      // first synchronize rings compliance
      while (true && cpt < MAX_RETRY) {
        cpt++;
        const batch = await ringSyncService.getUnsyncedBatch(BATCH_SIZE);
        if (batch.length === 0) {
          break;
        }

        const body = {
          authorId: user?.id,
          rings: batch,
        };

        await fetchRingValidationV1ControllerUploadRingCompliance({
          pathParams: { constructionSiteId: currentConstructionSiteId },
          body: body,
          headers: restHeaders,
        });

        await ringSyncService.markAsSynchronized(batch.map((r) => r.id));
      }

      cpt = 0;
      // then defect reports
      while (true && cpt < MAX_RETRY) {
        cpt++;
        const batch =
          await defectReportSyncService.getUnsyncedBatch(BATCH_SIZE);
        if (batch.length === 0) {
          break;
        }

        await fetchDefectReportV1ControllerUpdateDefectReports({
          pathParams: { constructionSiteId: currentConstructionSiteId },
          body: batch,
          headers: restHeaders,
        });

        await defectReportSyncService.markAsSynchronized(
          batch.map((defect) => defect.id),
        );
      }

      cpt = 0;
      // then repair reports
      while (true && cpt < MAX_RETRY) {
        cpt++;
        const batch =
          await repairReportSyncService.getUnsyncedBatch(BATCH_SIZE);
        if (batch.length === 0) {
          break;
        }

        const body = {
          authorId: user?.id,
          repairs: batch,
        };

        await fetchRepairReportV1ControllerUploadRepairs({
          pathParams: { constructionSiteId: currentConstructionSiteId },
          body: body,
          headers: restHeaders,
        });

        await repairReportSyncService.markAsSynchronized(
          batch.map((repair) => repair.id),
        );
      }

      cpt = 0;
      // then defect report pictures
      while (true && cpt < MAX_RETRY) {
        cpt++;
        const batch =
          await defectReportPictureSyncService.getUnsyncedBatch(
            PICTURE_BATCH_SIZE,
          );
        if (batch.length === 0) {
          break;
        }

        const formData = new FormData();
        for (let i = 0; i < batch.length; i++) {
          const picture = batch[i];
          const { blob, extension } = base64DataToBlob(picture.data);
          formData.append(
            'pictures',
            blob,
            `${picture.defectReportId}-picture-${i}.${extension}`,
          );
        }

        await fetchDefectReportPictureV1ControllerUpload({
          headers: { ...restHeaders, 'Content-Type': 'multipart/form-data' },
          body: formData,
        } as unknown as Parameters<
          typeof fetchDefectReportPictureV1ControllerUpload
        >[0]);

        await defectReportPictureSyncService.markAsSynchronized(
          batch.map((picture) => picture.id),
        );
      }

      cpt = 0;
      // then defect report pictures removed during the session
      while (true && cpt < MAX_RETRY) {
        cpt++;
        const batch =
          await defectReportPictureSyncService.getUnsyncedDeletedBatch(
            BATCH_SIZE,
          );
        if (batch.length === 0) {
          break;
        }

        await fetchDefectReportPictureV1ControllerDeleteBatch({
          body: { ids: batch.map((picture) => picture.id) },
          headers: restHeaders,
        });

        await defectReportPictureSyncService.deleteLocalBatch(
          batch.map((picture) => picture.id),
        );
      }

      cpt = 0;
      // then repair report pictures
      while (true && cpt < MAX_RETRY) {
        cpt++;
        const batch =
          await repairReportPictureSyncService.getUnsyncedBatch(
            PICTURE_BATCH_SIZE,
          );
        if (batch.length === 0) {
          break;
        }

        const formData = new FormData();
        for (let i = 0; i < batch.length; i++) {
          const picture = batch[i];
          const { blob, extension } = base64DataToBlob(picture.data);
          formData.append(
            'pictures',
            blob,
            `${picture.repairReportId}-picture-${i}.${extension}`,
          );
        }

        await fetchRepairReportPictureV1ControllerUpload({
          headers: { ...restHeaders, 'Content-Type': 'multipart/form-data' },
          body: formData,
        } as unknown as Parameters<
          typeof fetchRepairReportPictureV1ControllerUpload
        >[0]);

        await repairReportPictureSyncService.markAsSynchronized(
          batch.map((picture) => picture.id),
        );
      }

      cpt = 0;
      // finally repair report pictures removed during the session
      while (true && cpt < MAX_RETRY) {
        cpt++;
        const batch =
          await repairReportPictureSyncService.getUnsyncedDeletedBatch(
            BATCH_SIZE,
          );
        if (batch.length === 0) {
          break;
        }

        await fetchRepairReportPictureV1ControllerDeleteBatch({
          body: { ids: batch.map((picture) => picture.id) },
          headers: restHeaders,
        });

        await repairReportPictureSyncService.deleteLocalBatch(
          batch.map((picture) => picture.id),
        );
      }

      await utilsDbService.resetData();
      setBoardConstructionSiteId(undefined);

      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      setIsUploading(false);
    }
  };

  return { isUploading, upload };
};
